import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Copy, Ticket } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { GRANT_TIERS, formatDate, formatDateTime } from "@/lib/billingGrant";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";
import {
  createPromoCode,
  listPromoCodes,
  revokePromoCode,
  type PromoCode,
  type PromoCodeStatus,
} from "@/services/promoCodesApi";

const STATUS_LABEL: Record<PromoCodeStatus, string> = {
  available: "Disponible",
  redeemed: "Utilisé",
  expired: "Expiré",
  revoked: "Révoqué",
};

const STATUS_COLOR: Record<PromoCodeStatus, string> = {
  available: "bg-emerald-100 text-emerald-800",
  redeemed: "bg-sky-100 text-sky-800",
  expired: "bg-gray-100 text-gray-700",
  revoked: "bg-red-100 text-red-800",
};

type StatusFilter = "" | PromoCodeStatus;

function StatusCell({ code }: { code: PromoCode }) {
  const badge = (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[code.status]}`}>
      {STATUS_LABEL[code.status]}
    </span>
  );
  if (code.status === "redeemed") {
    return (
      <div className="space-y-0.5">
        {badge}
        <div className="text-xs text-gray-600">
          par{" "}
          {code.redeemed_organization_id ? (
            <Link
              to={`/organizations/${code.redeemed_organization_id}`}
              className="text-blue-700 underline"
            >
              {code.redeemed_organization_name ?? `organisation #${code.redeemed_organization_id}`}
            </Link>
          ) : (
            "une organisation supprimée"
          )}{" "}
          le {formatDateTime(code.redeemed_at)}
        </div>
        {code.redeemed_by_email && (
          <div className="text-xs text-gray-500">{code.redeemed_by_email}</div>
        )}
      </div>
    );
  }
  if (code.status === "revoked") {
    return (
      <div className="space-y-0.5">
        {badge}
        <div className="text-xs text-gray-600">
          le {formatDateTime(code.revoked_at)} par {code.revoked_by}
        </div>
      </div>
    );
  }
  if (code.status === "expired") {
    return (
      <div className="space-y-0.5">
        {badge}
        <div className="text-xs text-gray-600">le {formatDateTime(code.expires_at)}</div>
      </div>
    );
  }
  return badge;
}

/** The created code, shown ONCE (backoffice only stores its hash). */
function CreatedCode({ code, onClose }: { code: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <section
      className="rounded border border-emerald-300 bg-emerald-50 p-4"
      data-testid="created-code"
    >
      <p className="text-sm font-semibold text-emerald-900">Code créé</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <code className="rounded bg-white px-3 py-2 font-mono text-lg tracking-wider">{code}</code>
        <Button size="sm" variant="outline" onClick={() => void copy()}>
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Copié" : "Copier"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          J'ai noté le code
        </Button>
      </div>
      <p className="mt-2 text-xs text-emerald-900">
        Ce code ne sera plus affiché : seule son empreinte est conservée. Copiez-le maintenant
        et transmettez-le au client.
      </p>
    </section>
  );
}

/**
 * Codes promo à usage unique : un client qui s'inscrit le saisit sur la page
 * d'abonnement et obtient un « Accès offert » (tier, sièges, durée) sans payer.
 */
export default function PromoCodesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Formulaire
  const [tier, setTier] = useState<string>("business");
  const [seatLimit, setSeatLimit] = useState("");
  const [unlimited, setUnlimited] = useState(false);
  const [grantDays, setGrantDays] = useState("30");
  const [noExpiry, setNoExpiry] = useState(false);
  const [validDays, setValidDays] = useState("30");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPromoCodes(navigate, statusFilter ? { status: statusFilter } : {});
      setItems(res.items);
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Chargement impossible",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, statusFilter, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const formValid =
    note.trim().length >= 3 &&
    (unlimited || Number(grantDays) >= 1) &&
    (noExpiry || (Number(validDays) >= 1 && Number(validDays) <= 365)) &&
    (!seatLimit.trim() || Number(seatLimit) >= 1);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formValid || busy) return;
    setBusy(true);
    try {
      const result = await createPromoCode(
        {
          tier,
          seat_limit: seatLimit.trim() ? Number(seatLimit) : null,
          grant_days: unlimited ? null : Number(grantDays),
          unlimited,
          valid_days: noExpiry ? null : Number(validDays),
          note: note.trim(),
        },
        navigate
      );
      setCreated(result.code);
      setNote("");
      await load();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Création impossible",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (code: PromoCode) => {
    const confirmed = await confirmDialog({
      title: "Révoquer le code promo",
      description: `Le code ${code.code_prefix}-… ne pourra plus être utilisé.`,
      confirmLabel: "Révoquer",
      destructive: true,
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await revokePromoCode(code.id, navigate);
      toast({ title: "Code révoqué" });
      await load();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Révocation impossible",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Codes promo</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-6xl">
          <section className="border rounded p-4 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
              <Ticket size={18} /> Nouveau code promo
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Code à usage unique. Le client le saisit après son inscription, sur la page
              d'abonnement : son organisation reçoit un accès offert, sans paiement.
            </p>
            <form onSubmit={(e) => void submit(e)} className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="promo-tier">Tier</Label>
                <select
                  id="promo-tier"
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                >
                  {GRANT_TIERS.map((t) => (
                    <option key={t} value={t}>
                      {tierBadgeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="promo-seats">Plafond de sièges (vide = celui du tier)</Label>
                <Input
                  id="promo-seats"
                  type="number"
                  min={1}
                  value={seatLimit}
                  onChange={(e) => setSeatLimit(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    id="promo-unlimited"
                    type="checkbox"
                    checked={unlimited}
                    onChange={(e) => setUnlimited(e.target.checked)}
                  />
                  <Label htmlFor="promo-unlimited">Accès illimité (sans date de fin)</Label>
                </div>
                {!unlimited && (
                  <>
                    <Label htmlFor="promo-grant-days">Durée de l'accès (jours)</Label>
                    <Input
                      id="promo-grant-days"
                      type="number"
                      min={1}
                      value={grantDays}
                      onChange={(e) => setGrantDays(e.target.value)}
                    />
                  </>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    id="promo-no-expiry"
                    type="checkbox"
                    checked={noExpiry}
                    onChange={(e) => setNoExpiry(e.target.checked)}
                  />
                  <Label htmlFor="promo-no-expiry">Code utilisable sans limite de temps</Label>
                </div>
                {!noExpiry && (
                  <>
                    <Label htmlFor="promo-valid-days">Validité du code (jours, 365 max)</Label>
                    <Input
                      id="promo-valid-days"
                      type="number"
                      min={1}
                      max={365}
                      value={validDays}
                      onChange={(e) => setValidDays(e.target.value)}
                    />
                  </>
                )}
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="promo-note">Pour qui (obligatoire)</Label>
                <Textarea
                  id="promo-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ex. Jeanne Martin, cabinet Acme — rencontrée au salon RH"
                />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={busy || !formValid}>
                  Créer le code
                </Button>
              </div>
            </form>
          </section>

          {created && <CreatedCode code={created} onClose={() => setCreated(null)} />}

          <section className="border rounded p-4 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-lg font-semibold">Codes</h2>
              <div className="flex items-center gap-2">
                <label htmlFor="promo-status-filter" className="text-sm text-gray-600">
                  Statut
                </label>
                <select
                  id="promo-status-filter"
                  className="border rounded px-2 py-1 text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                >
                  <option value="">Tous</option>
                  <option value="available">Disponibles</option>
                  <option value="redeemed">Utilisés</option>
                  <option value="expired">Expirés</option>
                  <option value="revoked">Révoqués</option>
                </select>
              </div>
            </div>
            {loading && <div className="text-gray-500 text-sm">Chargement...</div>}
            {!loading && items.length === 0 && (
              <p className="text-sm text-gray-500">Aucun code promo.</p>
            )}
            {items.length > 0 && (
              <div className="overflow-auto">
                <table className="min-w-full text-sm" data-testid="promo-codes">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Code</th>
                      <th className="px-3 py-2 text-left">Pour</th>
                      <th className="px-3 py-2 text-left">Accès</th>
                      <th className="px-3 py-2 text-left">Validité du code</th>
                      <th className="px-3 py-2 text-left">Statut</th>
                      <th className="px-3 py-2 text-left">Créé</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id} className="border-t align-top">
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{c.code_prefix}-…</td>
                        <td className="px-3 py-2 max-w-xs break-words">{c.note}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded px-2 py-0.5 text-xs font-medium ${tierBadgeColor(c.tier)}`}
                          >
                            {tierBadgeLabel(c.tier)}
                          </span>
                          <div className="text-xs text-gray-600 mt-1">
                            {c.unlimited ? "Illimité" : `${c.grant_days} j`}
                            {" · "}
                            {c.seat_limit ? `${c.seat_limit} sièges` : "sièges du tier"}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {c.expires_at ? `jusqu'au ${formatDate(c.expires_at)}` : "Sans limite"}
                        </td>
                        <td className="px-3 py-2">
                          <StatusCell code={c} />
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                          {formatDate(c.created_at)}
                          <div>{c.created_by}</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {c.status === "available" && (
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={busy}
                              onClick={() => void revoke(c)}
                            >
                              Révoquer
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
